import React from 'react';
import {
  Grid, Card, CardContent, Typography, Box, Skeleton, Chip,
} from '@mui/material';
import {
  FlightTakeoff, DirectionsRun, CheckCircle, Cancel, People,
  Payment, Hotel, DirectionsCar, Warning, Assignment,
} from '@mui/icons-material';

const KPICard = ({ title, value, icon: Icon, color, loading, badge, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      border: '1px solid',
      borderColor: `${color}.light`,
      borderRadius: 2,
      transition: 'transform 0.15s, box-shadow 0.15s',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 4 } : {},
      height: '100%',
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
            {title}
          </Typography>
          {loading ? (
            <Skeleton width={60} height={40} />
          ) : (
            <Typography variant="h4" fontWeight={700} color={`${color}.main`} mt={0.5}>
              {value ?? '—'}
            </Typography>
          )}
          {badge && !loading && (
            <Chip label={badge} size="small" color={color} variant="outlined" sx={{ mt: 0.5 }} />
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}.50`,
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color: `${color}.main`, fontSize: 28 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ── Manager KPI Cards ──────────────────────────────────────────────────────────
export const ManagerKPICards = ({ data, loading, onFilterClick }) => {
  const cards = [
    { key: 'total_trips',                 title: 'Total Trips',               icon: Assignment,    color: 'primary',  status: 'All' },
    { key: 'upcoming',                    title: 'Upcoming Trips',             icon: FlightTakeoff, color: 'info',     status: 'Upcoming' },
    { key: 'running',                     title: 'Running Trips',              icon: DirectionsRun, color: 'success',  status: 'Running' },
    { key: 'completed',                   title: 'Completed Trips',            icon: CheckCircle,   color: 'success',  status: 'Completed' },
    { key: 'cancelled',                   title: 'Cancelled Trips',            icon: Cancel,        color: 'error',    status: 'Cancelled' },
    { key: 'total_guests_today',          title: 'Guests Travelling Today',    icon: People,        color: 'secondary' },
    { key: 'pending_payment_collections', title: 'Pending Collections',        icon: Payment,       color: 'warning' },
    { key: 'pending_hotel_confirmations', title: 'Pending Hotel Confirm.',     icon: Hotel,         color: 'warning' },
    { key: 'pending_driver_confirmations',title: 'Pending Driver Confirm.',    icon: DirectionsCar, color: 'warning' },
    { key: 'urgent_follow_ups',           title: 'Urgent Follow-Ups',          icon: Warning,       color: 'error' },
  ];

  return (
    <Grid container spacing={2}>
      {cards.map(({ key, title, icon, color, status }) => (
        <Grid item xs={12} sm={6} md={4} lg={2.4} key={key}>
          <KPICard
            title={title}
            value={data?.[key]}
            icon={icon}
            color={color}
            loading={loading}
            onClick={status ? () => onFilterClick?.(status) : undefined}
          />
        </Grid>
      ))}
    </Grid>
  );
};

// ── Executive KPI Cards ────────────────────────────────────────────────────────
export const ExecutiveKPICards = ({ data, loading }) => {
  const cards = [
    { key: 'my_assigned_trips',    title: 'My Assigned Trips',     icon: Assignment,    color: 'primary' },
    { key: 'upcoming',             title: 'Upcoming',               icon: FlightTakeoff, color: 'info' },
    { key: 'running',              title: 'Running',                icon: DirectionsRun, color: 'success' },
    { key: 'completed',            title: 'Completed',              icon: CheckCircle,   color: 'success' },
    { key: 'pending_confirmations',title: 'Pending Confirmations',  icon: Hotel,         color: 'warning' },
    { key: 'pending_collections',  title: 'Pending Collections',    icon: Payment,       color: 'error' },
  ];

  return (
    <Grid container spacing={2}>
      {cards.map(({ key, title, icon, color }) => (
        <Grid item xs={12} sm={6} md={4} lg={2} key={key}>
          <KPICard
            title={title}
            value={data?.[key]}
            icon={icon}
            color={color}
            loading={loading}
          />
        </Grid>
      ))}
    </Grid>
  );
};
